import { useRef, useState } from 'react';

interface Product {
  id: string;
  name: string;
  image: string;
  price: string;
  link: string;
}

interface ProductCarouselProps {
  categoryName: string;
  products: Product[];
}

export default function ProductCarousel({ categoryName, products }: ProductCarouselProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [scrollLeft, setScrollLeft] = useState(0);
  const [scrollWidth, setScrollWidth] = useState(0);
  const [clientWidth, setClientWidth] = useState(0);

  const handleScroll = () => {
    if (scrollRef.current) {
      setScrollLeft(scrollRef.current.scrollLeft);
      setScrollWidth(scrollRef.current.scrollWidth);
      setClientWidth(scrollRef.current.clientWidth);
    }
  };

  const scrollBy = (direction: 'left' | 'right') => {
    if (scrollRef.current) {
      const scrollAmount = 280;
      scrollRef.current.scrollBy({ left: direction === 'left' ? -scrollAmount : scrollAmount, behavior: 'smooth' });
    }
  };

  const showLeft = scrollLeft > 10;
  const showRight = scrollLeft + clientWidth < scrollWidth - 10;

  return (
    <div className="py-8 md:py-12">
      <div className="flex items-center justify-between px-4 md:px-6 lg:px-12 xl:px-20 mb-6">
        <h2 className="text-xl md:text-2xl font-bold text-foreground-950 tracking-tight">
          {categoryName}
        </h2>
        <div className="flex items-center gap-2">
          <button
            onClick={() => scrollBy('left')}
            className={`w-9 h-9 md:w-10 md:h-10 rounded-full border border-background-200 flex items-center justify-center transition-all hover:bg-background-100 hover:scale-105 ${showLeft ? 'opacity-100' : 'opacity-30 cursor-default'}`}
            aria-label="Scroll left"
          >
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-arrow-left-s-line text-foreground-700 text-lg" />
            </div>
          </button>
          <button
            onClick={() => scrollBy('right')}
            className={`w-9 h-9 md:w-10 md:h-10 rounded-full border border-background-200 flex items-center justify-center transition-all hover:bg-background-100 hover:scale-105 ${showRight ? 'opacity-100' : 'opacity-30 cursor-default'}`}
            aria-label="Scroll right"
          >
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-arrow-right-s-line text-foreground-700 text-lg" />
            </div>
          </button>
        </div>
      </div>
      <div
        ref={scrollRef}
        onScroll={handleScroll}
        className="flex gap-3 md:gap-4 overflow-x-auto scrollbar-hide px-4 md:px-6 lg:px-12 xl:px-20 pb-4"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        {products.map((product) => (
          <a
            key={product.id}
            href={product.link}
            target="_blank"
            rel="noopener nofollow"
            className="group flex-shrink-0 w-[200px] md:w-[240px] lg:w-[260px] bg-background-50 rounded-lg border border-background-200/80 overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:shadow-lg hover:border-primary-200 cursor-pointer"
          >
            <div className="relative w-full aspect-square overflow-hidden bg-background-100">
              <img
                src={product.image}
                alt={product.name}
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                loading="lazy"
              />
              <div className="absolute top-2 right-2 bg-primary-500 text-background-50 text-xs font-bold px-2 py-1 rounded-md">
                {product.price}
              </div>
            </div>
            <div className="p-3 md:p-4">
              <h3 className="text-sm font-medium text-foreground-900 leading-tight line-clamp-2 min-h-[2.5em] mb-3">
                {product.name}
              </h3>
              <div className="flex items-center justify-between">
                <span className="text-base font-bold text-primary-600">
                  {product.price}
                </span>
                <span className="text-xs bg-primary-100 text-primary-700 px-2 py-1 rounded-full font-medium flex items-center gap-1">
                  Buy Now
                  <div className="w-3 h-3 flex items-center justify-center">
                    <i className="ri-external-link-line text-xs" />
                  </div>
                </span>
              </div>
            </div>
          </a>
        ))}
      </div>
    </div>
  );
}